import axios from 'axios'

const LS_FILTERS_KEY = 'tenders.filters.v1'
const LS_SHOW_KEY = 'tenders.showFilters.v1'

function loadJSON(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback
  } catch {
    return fallback
  }
}
function saveJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* empty */
  }
}

const savedFilters = loadJSON(LS_FILTERS_KEY, {
  assortments: [],
  serviceTypes: [],
  page: 1,
})
const savedShow = !!loadJSON(LS_SHOW_KEY, true) // default open

const state = {
  loading: false,
  list: [], // tenders list used by tenders.vue
  pagination: null, // optional: { page, per_page, total, last_page }
  assortments: [], // TenderAssortment options
  serviceTypes: [], // TenderServiceType options
  showFilters: savedShow,
  filters: savedFilters,
}

const getters = {
  loading: (s) => s.loading,
  list: (s) => s.list,
  pagination: (s) => s.pagination,
  assortments: (s) => s.assortments,
  serviceTypes: (s) => s.serviceTypes,
  filters: (s) => s.filters,
  getShowFilters: (s) => s.showFilters,
}

const mutations = {
  setLoading(state, v) {
    state.loading = v
  },
  setList(state, payload) {
    if (Array.isArray(payload)) {
      state.list = payload
      state.pagination = null
    } else {
      state.list = payload.data || []
      state.pagination = payload.meta || null
    }
  },

  setAssortments(state, items) {
    state.assortments = items || []
  },
  setServiceTypes(state, items) {
    state.serviceTypes = items || []
  },
  setShowFilters(state, show) {
    state.showFilters = !!show
    saveJSON(LS_SHOW_KEY, state.showFilters)
  },

  setFilters(state, next) {
    state.filters = { ...state.filters, ...next }
    // reset page to 1 when changing ids
    if (next.assortments !== undefined || next.serviceTypes !== undefined) {
      state.filters.page = 1
    }
    saveJSON(LS_FILTERS_KEY, state.filters)
  },
  resetPage(state) {
    state.filters.page = 1
    saveJSON(LS_FILTERS_KEY, state.filters)
  },
}

const actions = {
  // Load filter options
  async loadFilters({ dispatch }) {
    await Promise.all([
      dispatch('loadAssortments'),
      dispatch('loadServiceTypes'),
    ])
  },

  async loadAssortments({ commit }) {
    const { data } = await axios.get('/api/tenders/assortments')
    // Expecting array like: [{ id, name }, ...]
    commit('setAssortments', data || [])
  },

  async loadServiceTypes({ commit }) {
    const { data } = await axios.get('/api/tenders/service-types')
    // Expecting array like: [{ id, name }, ...]
    commit('setServiceTypes', data || [])
  },

  // Update selected filters (doesn't auto-fetch; caller can decide)
  updateFilters({ commit }, next) {
    commit('setFilters', next)
  },

  // Main fetch used by tenders.vue
  async fetch({ state, commit }, maybeOverrides = {}) {
    commit('setLoading', true)
    try {
      const payload = {
        assortments: maybeOverrides.assortments ?? state.filters.assortments,
        service_types:
          maybeOverrides.serviceTypes ?? state.filters.serviceTypes,
        page: maybeOverrides.page ?? state.filters.page,
      }

      const { data } = await axios.post('/api/tenders/list', payload)

      // Accept either:
      // 1) { data: [ ... ], meta: { ...pagination } }
      // 2) [ ... ]
      commit('setList', data)
      // If caller forced page, keep it in state
      if (payload.page !== state.filters.page) {
        commit('setFilters', { page: payload.page })
      }
      return data
    } finally {
      commit('setLoading', false)
    }
  },

  // Helpers for UI
  toggleShowFilters({ commit, state }, show) {
    commit('setShowFilters', show === undefined ? !state.showFilters : show)
  },

  // Pagination helper (keeps current filters)
  async goToPage({ dispatch, state }, page) {
    await dispatch('fetch', {
      page,
      assortments: state.filters.assortments,
      serviceTypes: state.filters.serviceTypes,
    })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
